import { useEffect, useState } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Loader2, TrendingUp, Beaker, Save } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminPaywallPage() {
  const [variants, setVariants] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [savingKey, setSavingKey] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [v, f] = await Promise.all([
        api.get('/admin/paywall/variants'),
        api.get(`/admin/conversion-funnel?days=${days}`),
      ]);
      setVariants(v.data.variants || []);
      setFunnel(f.data);
    } catch (e) {
      toast.error('Failed to load paywall data — admin only');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [days]);

  const updateField = (key, field, value) => {
    setVariants(vs => vs.map(v => v.key === key ? { ...v, [field]: value } : v));
  };

  const save = async (variant) => {
    setSavingKey(variant.key);
    try {
      await api.post('/admin/paywall/variants', {
        key: variant.key,
        preview_businesses: Number(variant.preview_businesses),
        preview_districts: Number(variant.preview_districts),
        banner_copy: variant.banner_copy,
        headline_copy: variant.headline_copy,
        cta_copy: variant.cta_copy,
        allocated_pct: Number(variant.allocated_pct),
        active: variant.active,
      });
      toast.success(`Variant "${variant.key}" saved`);
      await refresh();
    } catch {
      toast.error('Save failed');
    } finally {
      setSavingKey(null);
    }
  };

  if (loading) return <div className="min-h-[400px] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-ink/30" /></div>;

  const summary = funnel?.summary || [];
  const allocSum = variants.filter(v => v.active).reduce((a, v) => a + Number(v.allocated_pct || 0), 0);

  return (
    <div className="max-w-6xl mx-auto px-6 py-8" data-testid="admin-paywall-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="font-body text-xs text-ink/50 uppercase tracking-wide mb-1 flex items-center gap-1.5"><Beaker className="w-3.5 h-3.5" /> A/B testing</p>
          <h1 className="font-display text-3xl font-extrabold text-ink">Paywall optimisation</h1>
          <p className="font-body text-sm text-ink/55 mt-1">Tune the freemium preview size and copy. Watch which variant converts free users best.</p>
        </div>
        <div className="flex items-center gap-2">
          <Label className="font-body text-xs text-ink/50">Window:</Label>
          <select className="border border-ink/15 rounded-[4px] px-2 py-1.5 font-body text-sm" value={days} onChange={(e) => setDays(Number(e.target.value))}>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>
      </div>

      {/* Funnel summary */}
      <div className="rounded-[6px] border border-ink/10 bg-white p-5 mb-6" data-testid="funnel-summary">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="w-4 h-4 text-brand-orange" />
          <h2 className="font-display text-base font-extrabold text-ink">Conversion funnel</h2>
        </div>
        {summary.length === 0 ? (
          <p className="font-body text-sm text-ink/50">No conversion events yet — events are logged automatically as users view the paywall and click upgrade CTAs.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-ink/[0.03] border-b border-ink/10">
                <tr>
                  <th className="font-display text-[10px] uppercase tracking-wider text-ink/60 p-2.5 text-left">Variant</th>
                  <th className="font-display text-[10px] uppercase tracking-wider text-ink/60 p-2.5 text-right">Views</th>
                  <th className="font-display text-[10px] uppercase tracking-wider text-ink/60 p-2.5 text-right">CTA Clicks</th>
                  <th className="font-display text-[10px] uppercase tracking-wider text-ink/60 p-2.5 text-right">CTR</th>
                  <th className="font-display text-[10px] uppercase tracking-wider text-ink/60 p-2.5 text-right">Checkouts</th>
                  <th className="font-display text-[10px] uppercase tracking-wider text-ink/60 p-2.5 text-right">Subscribed</th>
                  <th className="font-display text-[10px] uppercase tracking-wider text-ink/60 p-2.5 text-right">Conv. rate</th>
                </tr>
              </thead>
              <tbody>
                {summary.map((row) => (
                  <tr key={row.variant_key} className="border-b border-ink/5 last:border-0">
                    <td className="p-2.5 font-display text-sm font-extrabold text-ink">{row.variant_key}</td>
                    <td className="p-2.5 font-body text-sm text-ink/80 text-right">{row.paywall_views.toLocaleString()}</td>
                    <td className="p-2.5 font-body text-sm text-ink/80 text-right">{row.cta_clicks.toLocaleString()}</td>
                    <td className="p-2.5 font-body text-sm text-brand-orange font-extrabold text-right">{row.ctr_pct}%</td>
                    <td className="p-2.5 font-body text-sm text-ink/80 text-right">{row.checkout_started.toLocaleString()}</td>
                    <td className="p-2.5 font-body text-sm text-ink/80 text-right">{row.subscribed.toLocaleString()}</td>
                    <td className="p-2.5 font-display text-sm font-extrabold text-emerald-700 text-right">{row.subscribe_rate_pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Variants */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-base font-extrabold text-ink">Variants</h2>
          <p className={`font-body text-xs ${allocSum === 100 ? 'text-emerald-700' : 'text-amber-700'}`}>Active allocation: {allocSum}% {allocSum !== 100 && '(should be 100%)'}</p>
        </div>
        {variants.map((v) => (
          <div key={v.key} className="rounded-[6px] border border-ink/10 bg-white p-5" data-testid={`variant-${v.key}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <h3 className="font-display text-lg font-extrabold text-ink">{v.key}</h3>
                <span className={`font-body text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${v.active ? 'bg-emerald-500/10 text-emerald-700' : 'bg-ink/10 text-ink/50'}`}>{v.active ? 'Active' : 'Paused'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={!!v.active} onCheckedChange={(checked) => updateField(v.key, 'active', checked)} data-testid={`variant-active-${v.key}`} />
                <Label className="font-body text-xs text-ink/55">{v.active ? 'Active' : 'Paused'}</Label>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <div>
                <Label className="font-body text-xs text-ink/55">Preview businesses (top-N)</Label>
                <Input type="number" min={1} max={20} value={v.preview_businesses} onChange={(e) => updateField(v.key, 'preview_businesses', e.target.value)} className="font-body text-sm" data-testid={`variant-preview-businesses-${v.key}`} />
              </div>
              <div>
                <Label className="font-body text-xs text-ink/55">Preview districts (top-N)</Label>
                <Input type="number" min={1} max={9} value={v.preview_districts} onChange={(e) => updateField(v.key, 'preview_districts', e.target.value)} className="font-body text-sm" />
              </div>
              <div>
                <Label className="font-body text-xs text-ink/55">Allocated traffic (%)</Label>
                <Input type="number" min={0} max={100} value={v.allocated_pct} onChange={(e) => updateField(v.key, 'allocated_pct', e.target.value)} className="font-body text-sm" data-testid={`variant-allocated-${v.key}`} />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 mb-3">
              <div>
                <Label className="font-body text-xs text-ink/55">Banner copy (Priority Matrix)</Label>
                <Input value={v.banner_copy || ''} onChange={(e) => updateField(v.key, 'banner_copy', e.target.value)} className="font-body text-sm" />
              </div>
              <div>
                <Label className="font-body text-xs text-ink/55">Headline copy (Intervention Plan)</Label>
                <Input value={v.headline_copy || ''} onChange={(e) => updateField(v.key, 'headline_copy', e.target.value)} className="font-body text-sm" />
              </div>
              <div>
                <Label className="font-body text-xs text-ink/55">CTA button copy</Label>
                <Input value={v.cta_copy || ''} onChange={(e) => updateField(v.key, 'cta_copy', e.target.value)} className="font-body text-sm" />
              </div>
            </div>
            <Button onClick={() => save(v)} disabled={savingKey === v.key} className="bg-ink hover:bg-ink/90 text-white font-body text-sm" data-testid={`variant-save-${v.key}`}>
              {savingKey === v.key ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />} Save
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
